export type ImageFrame = ImageBitmap | HTMLImageElement;

// Cap preloaded bitmaps at this width to avoid holding gigabytes of decoded
// pixel data in memory (high-res AR source images can be 3000 px+).
export const MAX_PRELOAD_WIDTH = 768;

export const closeImageFrame = (frame: ImageFrame | undefined) => {
  if (frame instanceof ImageBitmap) {
    try { frame.close(); } catch { /* ignore if already closed */ }
  }
};

export const downscaleToMaxWidth = (img: HTMLImageElement): Promise<ImageFrame> => {
  const { naturalWidth, naturalHeight } = img;
  if (naturalWidth === 0 || naturalHeight === 0) {
    return Promise.reject(new Error("Image has no dimensions"));
  }

  const scale = Math.min(1, MAX_PRELOAD_WIDTH / naturalWidth);
  const width = Math.max(1, Math.round(naturalWidth * scale));
  const height = Math.max(1, Math.round(naturalHeight * scale));

  if (scale >= 1) {
    if (typeof createImageBitmap === "function") {
      return createImageBitmap(img);
    }
    return Promise.resolve(img);
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return Promise.reject(new Error("Could not get 2d context for downscale"));
  }
  ctx.drawImage(img, 0, 0, width, height);

  if (typeof createImageBitmap === "function") {
    return createImageBitmap(canvas);
  }

  return new Promise((resolve, reject) => {
    const scaled = new Image();
    scaled.onload = () => resolve(scaled);
    scaled.onerror = () => reject(new Error("Failed to load downscaled image"));
    scaled.src = canvas.toDataURL("image/jpeg", 0.85);
  });
};

export const loadFrameFromImage = (img: HTMLImageElement): Promise<ImageFrame> => {
  const needsDownscale = img.naturalWidth > MAX_PRELOAD_WIDTH;

  if (typeof createImageBitmap !== "function") {
    return needsDownscale ? downscaleToMaxWidth(img) : Promise.resolve(img);
  }

  if (needsDownscale) {
    return createImageBitmap(img, { resizeWidth: MAX_PRELOAD_WIDTH }).catch(() =>
      downscaleToMaxWidth(img)
    );
  }

  return createImageBitmap(img).catch(() => downscaleToMaxWidth(img));
};
